import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { FormLabel, FormGroup, FormControl, Badge } from 'react-bootstrap';
import { PropTypes } from 'prop-types';
import classNames from 'classnames';
import Mousetrap from 'mousetrap';
import { ClipLoader } from 'react-spinners';
import ApiService from '../../services/ApiService';
import FlatButton from './FlatButton';

const PartPane = props => {
  const { initialValue, updateValue, previous, list, validationCallback } = props;
  const [value, setValue] = useState(initialValue || '');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [lastGood, setLastGood] = useState(initialValue || '');

  const originalKey = Mousetrap.prototype.handleKey;
  const partRegEx = /^[A-Z]?:?[A-Z.#*0-9/-]+$/i;

  const clear = () => {
    setValue('');
  };

  const validate = item => {
    if (loading) {
      return;
    }

    item = item.trim();

    if (!partRegEx.test(item)) {
      setMessage('Invalid Part');
      return;
    }

    if (value !== item) {
      setValue(item);
    }

    setLoading(true);
    const params = {
      limit: 1,
      q: `itemid.keyword:/[A-Za-z]?:?${item
        .toUpperCase()
        .trim()
        .replace(/\*/, '\\*')
        .replace(/:/, '\\:')
        .replace(/\//, '\\/')}/`,
    };

    ApiService.get('list/PIM1,PIM4/multiSearch', { params }).then(response => {
      const part = _.get(response, 'data.list.0');
      if (!part) {
        setMessage(`The part ${value} was not found`);
        setLoading(false);
        return;
      }

      const altBins = part['2nd Bins'] ? part['2nd Bins'].split(' ') : [];
      const threshold = parseInt(part['On Hand'], 10);

      const pattern = new RegExp(`[A-Z]?:?${item}`, 'gi');
      const allBins = part.Bin ? [part.Bin, ...altBins] : [];

      // Example parts X:D786 K:1425868
      // Found part, now check for for POs they are associated with
      if (pattern.test(part.itemid)) {
        setLastGood(part.itemid);
        if (validationCallback) {
          validationCallback(part.itemid, setMessage).then(() => {
            setLoading(false);
            updateValue(part.itemid, allBins, threshold);
          });
        } else {
          setLoading(false);
          updateValue(part.itemid, allBins, threshold);
        }
      } else {
        setMessage(`The part ${value} was not found`);
        setLoading(false);
      }
    });
  };

  const onChange = event => {
    setValue(event.target.value);
  };

  const onKeyDown = event => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      validate(value);
    }
  };

  // const handlePartKey = key => {
  //   const entry = `${value}${key}`;
  //   setValue(entry);
  // };

  const handleKey = (char, m, e) => {
    if (e.target.tagName !== 'INPUT') {
      if (e.type === 'keydown') {
        if (e.key === 'Tab' || e.key === 'Enter') {
          validate(value);
          return;
        }
      }

      if (e.type === 'keypress' && (e.key !== 'Tab' && e.key !== 'Enter')) {
        setValue(val => `${val}${e.key}`.toUpperCase());
      }
    }
  };

  useEffect(() => {
    Mousetrap.prototype.handleKey = handleKey;

    return () => {
      Mousetrap.prototype.handleKey = originalKey;
    };
  });

  useEffect(() => {
    setMessage('');
  }, [value]);

  const renderSuggestions = () => {
    if (list.length === 0) {
      return null;
    }

    const allRecent = list.every(item => item.info === 'Recent');

    if (allRecent) {
      return <div className="all-recent">All parts in this bin have been counted</div>;
    }

    return (
      <div className="recent-wrapper">
        <div className="recent">
          <div className="recent-head">Suggestions</div>
          <div className="recent-body">
            {list.map(({ partNumber, info }) => {
              return (
                <div className="recent-item" key={partNumber} onClick={() => validate(partNumber)}>
                  {partNumber} <Badge>{info}</Badge>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const clearClassName = classNames({
    'clear-button': true,
    disabled: value === '' || loading,
  });

  const isGood = (lastGood !== '' && lastGood === value);

  const checkClassName = classNames({
    check: true,
    primary: lastGood !== value,
    disabled: loading || isGood,
    good: isGood,
  });

  const checkLabel = isGood ? <i className="fas fa-check-circle" /> : 'Check';

  return (
    <>
      {previous !== '' && (
        <div className="previous-part">
          Previous <strong>{previous}</strong>
        </div>
      )}
      <div className="entry">
        <FormLabel>Scan or Enter Part Number</FormLabel>
        <FormGroup>
          <FormControl onChange={onChange} onKeyDown={onKeyDown} value={value} />
          {(value.length || loading !== false) && (
            <div className="loading">
              <FlatButton className={checkClassName} onClick={() => validate(value)}>
                {loading ? <ClipLoader sizeUnit="px" size={12} width={2} color="#fff" /> : checkLabel}
              </FlatButton>
            </div>
          )}
          {message !== '' && <div className="message">{message}</div>}
        </FormGroup>
        <FlatButton className={clearClassName} onClick={clear}>
          Clear
        </FlatButton>
      </div>
      <div className="lists">{renderSuggestions()}</div>
    </>
  );
};

PartPane.defaultProps = {
  previous: '',
  list: [],
};

PartPane.propTypes = {
  initialValue: PropTypes.string,
  list: PropTypes.array,
  previous: PropTypes.string,
  updateValue: PropTypes.func,
  validationCallback: PropTypes.func,
};

export default PartPane;
